import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Container, Typography, Avatar, Chip, CircularProgress,
    Stack, Paper, Card, CardContent, Divider, TextField,
    Button, IconButton, Tooltip, Grid,
} from '@mui/material';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import StarIcon           from '@mui/icons-material/Star';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import AttachMoneyIcon    from '@mui/icons-material/AttachMoney';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import SaveIcon           from '@mui/icons-material/Save';
import PersonIcon         from '@mui/icons-material/Person';
import { useNavigate }    from 'react-router-dom';
import axios              from '../api/axiosConfig';
import axiosPublic        from '../api/axiosPublic';
import { useAuth }        from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';

const STATUS_COLORS = {
    SCHEDULED:  { bgcolor: '#e3f2fd', color: '#1565c0' },
    IN_PROGRESS:{ bgcolor: '#fff3e0', color: '#e65100' },
    COMPLETED:  { bgcolor: '#e8f5e9', color: '#2e7d32' },
    CANCELED:   { bgcolor: '#fce4ec', color: '#c62828' },
};

function StatCard({ icon, label, value, sub, color }) {
    return (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid rgba(196,98,45,0.1)',
            bgcolor: 'white', boxShadow: '0 2px 12px rgba(44,31,20,0.07)', flex: 1, minWidth: 140 }}>
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ color: color || TERRACOTTA }}>{icon}</Box>
                    <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary',
                        textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}>
                        {label}
                    </Typography>
                </Box>
                <Typography variant="h5" fontWeight={900} sx={{ color: WALNUT }}>{value}</Typography>
                {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
            </CardContent>
        </Card>
    );
}

function StarDisplay({ score }) {
    return (
        <Stack direction="row" spacing={0.3}>
            {[1,2,3,4,5].map(i => (
                <StarIcon key={i} sx={{ fontSize: '1rem',
                    color: i <= score ? GOLD : 'rgba(0,0,0,0.15)' }} />
            ))}
        </Stack>
    );
}

// Inline earnings setter for each tour row (managers see this; guides see read-only)
function EarningsCell({ tour, isManager, onSaved }) {
    const [pct,     setPct]     = useState(tour.earningsPercent?.toString() || '');
    const [saving,  setSaving]  = useState(false);

    const handleSave = async () => {
        if (!pct) return;
        setSaving(true);
        try {
            const res = await axios.put(`/api/tours/earnings/${tour.id}?percent=${pct}`);
            onSaved(res.data);
        } catch { /* silent */ }
        finally { setSaving(false); }
    };

    if (isManager) {
        return (
            <Stack direction="row" spacing={0.5} alignItems="center">
                <TextField size="small" value={pct} onChange={e => setPct(e.target.value)}
                    placeholder="%" type="number" inputProps={{ min: 0, max: 100, step: 0.5 }}
                    sx={{ width: 70 }} />
                <Tooltip title="Save earnings %">
                    <IconButton size="small" onClick={handleSave} disabled={saving || !pct}
                        sx={{ color: FOREST }}>
                        {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
                {tour.guideEarnings != null && (
                    <Typography variant="caption" fontWeight={700} sx={{ color: FOREST }}>
                        = {formatCurrency(tour.guideEarnings, tour.billCurrency || 'EUR')}
                    </Typography>
                )}
            </Stack>
        );
    }

    // Guide view — read-only
    if (tour.guideEarnings != null) {
        return (
            <Typography variant="body2" fontWeight={700} sx={{ color: FOREST }}>
                {formatCurrency(tour.guideEarnings, tour.billCurrency || 'EUR')}
                {tour.earningsPercent != null && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({tour.earningsPercent}%)
                    </Typography>
                )}
            </Typography>
        );
    }
    return <Typography variant="caption" color="text.disabled">—</Typography>;
}

export default function GuideDashboard() {
    const { user }  = useAuth();
    const navigate  = useNavigate();

    const isManager = user?.role === 'MANAGER' || user?.role === 'ADMIN';
    const isGuide   = user?.role === 'GUIDE';
    const canAccess = isGuide || isManager;

    // If a manager navigates here directly, show their own guide stats or block
    const guideId = user?.id;

    const [guide,   setGuide]   = useState(null);
    const [tours,   setTours]   = useState([]);
    const [ratings, setRatings] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!guideId) return;
        setLoading(true);
        try {
            const [toursRes, ratingsRes, summaryRes] = await Promise.all([
                axios.get(`/api/tours/view/guide/${guideId}`),
                axiosPublic.get(`/api/ratings/target?targetId=${guideId}&targetType=GUIDE`).catch(() => ({ data: [] })),
                axiosPublic.get(`/api/ratings/summary?targetId=${guideId}&targetType=GUIDE`).catch(() => ({ data: null })),
            ]);
            setTours(toursRes.data || []);
            setRatings(ratingsRes.data || []);
            setSummary(summaryRes.data);

            // Fetch guide profile for name/avatar
            try {
                const gRes = await axios.get(`/guides/view/id/${guideId}`);
                setGuide(gRes.data);
            } catch { /* not a guide — manager viewing their own */ }
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [guideId]);

    useEffect(() => { if (canAccess) load(); }, [load, canAccess]);

    if (!canAccess) return null;
    if (loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: TERRACOTTA }} />
        </Box>
    );

    const completedTours  = tours.filter(t => t.status === 'COMPLETED');
    const totalEarnings   = tours.reduce((s, t) => s + Number(t.guideEarnings || 0), 0);
    const avgRating       = summary?.averageScore?.toFixed(1) ?? '—';
    const ratingCount     = summary?.ratingCount ?? 0;

    const handleTourUpdated = (updated) => {
        setTours(prev => prev.map(t => t.id === updated.id ? updated : t));
    };

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://upload.wikimedia.org/wikipedia/commons/d/d5/Yerevan_Armenia_with_the_backdrop_of_Mount_Ararat.JPG") center/cover fixed` }}>
            {/* Banner */}
            <Box sx={{ height: 120, background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 50%, ${FOREST} 100%)`,
                position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: 36, background: PARCHMENT, borderRadius: '36px 36px 0 0' } }} />

            <Container maxWidth="lg" sx={{ mt: -3, pb: 8, position: 'relative', zIndex: 1 }}>
                {/* Back */}
                <Box sx={{ mb: 2 }}>
                    <IconButton onClick={() => navigate(-1)} size="small"
                        sx={{ color: FOREST, border: '1px solid rgba(74,103,65,0.25)', borderRadius: 2 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Profile header */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 3, display: 'flex',
                    alignItems: 'center', gap: 3, border: '1px solid rgba(196,98,45,0.1)',
                    bgcolor: 'white', boxShadow: '0 2px 16px rgba(44,31,20,0.07)' }}>
                    <Avatar src={guide?.imageUrl} alt={guide?.username || user?.username}
                        sx={{ width: 72, height: 72, bgcolor: FOREST,
                            border: '3px solid rgba(74,103,65,0.2)', flexShrink: 0 }}>
                        {(guide?.username || user?.username || '?')[0].toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h5" fontWeight={800}
                            sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {guide?.username || user?.username}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{guide?.email || user?.email}</Typography>
                        <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
                            <Chip label="GUIDE" size="small"
                                sx={{ bgcolor: FOREST, color: 'white', fontWeight: 700, fontSize: '0.7rem' }} />
                            {guide?.districtName && (
                                <Chip label={guide.districtName} size="small"
                                    sx={{ bgcolor: 'rgba(74,103,65,0.1)', color: FOREST, fontWeight: 600, fontSize: '0.7rem' }} />
                            )}
                        </Stack>
                    </Box>
                    {summary && ratingCount > 0 && (
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <StarDisplay score={Math.round(summary.averageScore)} />
                            <Typography variant="caption" color="text.secondary">
                                {avgRating} avg · {ratingCount} review{ratingCount !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    )}
                </Paper>

                {/* Stats row */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
                    <StatCard icon={<DirectionsBikeIcon />} label="Total Tours"
                        value={tours.length} color={TERRACOTTA} />
                    <StatCard icon={<CheckCircleIcon />} label="Completed"
                        value={completedTours.length} color={FOREST} />
                    <StatCard icon={<AttachMoneyIcon />} label="Total Earnings"
                        value={totalEarnings > 0
                            ? formatCurrency(totalEarnings, tours.find(t => t.billCurrency)?.billCurrency || 'EUR')
                            : '—'}
                        sub="sum across all tours" color={GOLD} />
                    <StatCard icon={<StarIcon />} label="Avg Rating"
                        value={ratingCount > 0 ? `${avgRating} ★` : '—'}
                        sub={ratingCount > 0 ? `${ratingCount} reviews` : 'No reviews yet'} color={GOLD} />
                </Stack>

                <Grid container spacing={3}>
                    {/* Tour history */}
                    <Grid item xs={12} lg={7}>
                        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden',
                            border: '1px solid rgba(196,98,45,0.1)', boxShadow: '0 2px 12px rgba(44,31,20,0.07)' }}>
                            <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.08)',
                                display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DirectionsBikeIcon sx={{ color: TERRACOTTA }} />
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                    Tour History
                                </Typography>
                                <Chip label={tours.length} size="small"
                                    sx={{ bgcolor: 'rgba(196,98,45,0.1)', color: TERRACOTTA, fontWeight: 700,
                                        height: 20, fontSize: '0.72rem', ml: 0.5 }} />
                            </Box>
                            <Box>
                                {tours.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                                        No tours yet.
                                    </Typography>
                                ) : (
                                    tours.map((tour, i) => {
                                        const sc = STATUS_COLORS[tour.status] || { bgcolor: '#f5f5f5', color: '#666' };
                                        return (
                                            <Box key={tour.id} sx={{
                                                px: 3, py: 2,
                                                bgcolor: i % 2 === 0 ? 'white' : 'rgba(247,243,238,0.7)',
                                                borderBottom: '1px solid rgba(0,0,0,0.04)',
                                                '&:last-child': { borderBottom: 'none' },
                                            }}>
                                                <Stack direction={{ xs: 'column', sm: 'row' }}
                                                    spacing={1.5} alignItems={{ sm: 'center' }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                                            {tour.offerTitle || 'Tour'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {tour.time
                                                                ? new Date(tour.time).toLocaleString('en-GB', {
                                                                    dateStyle: 'medium', timeStyle: 'short' })
                                                                : '—'}
                                                            {tour.type && ` · ${tour.type}`}
                                                        </Typography>
                                                    </Box>
                                                    <Chip label={tour.status} size="small"
                                                        sx={{ ...sc, fontWeight: 700, fontSize: '0.7rem', flexShrink: 0 }} />
                                                    <Box sx={{ flexShrink: 0 }}>
                                                        <EarningsCell tour={tour} isManager={isManager}
                                                            onSaved={handleTourUpdated} />
                                                    </Box>
                                                </Stack>
                                                {tour.billTotal && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Bill total: {formatCurrency(tour.billTotal, tour.billCurrency || 'EUR')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Customer reviews */}
                    <Grid item xs={12} lg={5}>
                        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden',
                            border: '1px solid rgba(196,98,45,0.1)', boxShadow: '0 2px 12px rgba(44,31,20,0.07)' }}>
                            <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.08)',
                                display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StarIcon sx={{ color: GOLD }} />
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                    Customer Reviews
                                </Typography>
                                <Chip label={ratings.length} size="small"
                                    sx={{ bgcolor: 'rgba(201,151,58,0.12)', color: GOLD, fontWeight: 700,
                                        height: 20, fontSize: '0.72rem', ml: 0.5 }} />
                            </Box>
                            <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
                                {ratings.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                                        No reviews yet.
                                    </Typography>
                                ) : (
                                    ratings.map((r, i) => (
                                        <Box key={r.id} sx={{
                                            px: 3, py: 2,
                                            borderBottom: i < ratings.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                                        }}>
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                <Avatar src={r.reviewerImageUrl} alt={r.reviewerName}
                                                    sx={{ width: 36, height: 36, bgcolor: TERRACOTTA, fontSize: '0.85rem', flexShrink: 0 }}>
                                                    <PersonIcon fontSize="small" />
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                        <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT }}>
                                                            {r.reviewerName || 'Customer'}
                                                        </Typography>
                                                        <StarDisplay score={r.score} />
                                                    </Stack>
                                                    {r.comment && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                            "{r.comment}"
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.disabled">
                                                        {r.createdAt
                                                            ? new Date(r.createdAt).toLocaleDateString('en-GB',
                                                                { day: 'numeric', month: 'short', year: 'numeric' })
                                                            : ''}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
